import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";

import DeleteIcon from "@material-ui/icons/Delete";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import { Skeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { assetUrl, readableError } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * addCarByAdmin and updateCarByAdmin take the same input, so the two
 * mutations are built from one shape. `color`, `brand`, `model`,
 * `carType`, `ride` and `delivery` are all non-null on it — the form
 * blocks on those rather than letting the server reject the save.
 * ------------------------------------------------------------------ */

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

const CAR_INPUT_VARS = `
  $color: ID!
  $brand: ID!
  $model: ID!
  $carType: ID!
  $ride: Boolean!
  $delivery: Boolean!
  $plate: String
  $manufacturingYear: Int
  $description: String
  $registrationDocumentUrl: String
  $pictures: pictures
  $insurance: InsuranceInput
  $carOptions: CarOptionsInput
`;

const CAR_INPUT_FIELDS = `
  color: $color
  brand: $brand
  model: $model
  carType: $carType
  ride: $ride
  delivery: $delivery
  plate: $plate
  manufacturingYear: $manufacturingYear
  description: $description
  registrationDocumentUrl: $registrationDocumentUrl
  pictures: $pictures
  insurance: $insurance
  carOptions: $carOptions
`;

const ADD_CAR = gql`
  mutation addCarByAdmin(${CAR_INPUT_VARS} $driverId: ID!) {
    addCarByAdmin(driverId: $driverId, input: { ${CAR_INPUT_FIELDS} }) {
      _id
      plate
    }
  }
`;

const EDIT_CAR = gql`
  mutation updateCarByAdmin(${CAR_INPUT_VARS} $carId: ID!) {
    updateCarByAdmin(carId: $carId, input: { ${CAR_INPUT_FIELDS} }) {
      _id
      plate
    }
  }
`;

const GET_LOOKUPS = gql`
  query getVehicleFormLookups {
    getCarBrands {
      _id
      name
    }
    getCarColors {
      _id
      name
      code
    }
    getCarTypes {
      _id
      type
    }
  }
`;

const GET_MODELS = gql`
  query getCarModelsForForm($brand: ID) {
    getCarModels(filters: { brand: $brand }) {
      _id
      name
    }
  }
`;

const GET_DRIVERS = gql`
  query getDriversForVehicleForm($fullName: String) {
    getDriversByAdmin(pagination: { limit: 20, skip: 0 }, filters: { fullName: $fullName }) {
      _id
      fullName
      phoneNumber
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

// Every flag on CarOptionsInput is non-null, so all eleven are always
// sent — the form just decides true or false for each.
const CAR_OPTIONS = [
  { key: "inHurry", label: "In a hurry", hint: "Passengers can pay to be prioritised." },
  {
    key: "orderingForSomeoneElse",
    label: "Ordering for someone else",
    hint: "The booker is not the passenger.",
  },
  { key: "pet", label: "Pets", hint: "Animals allowed in the vehicle." },
  { key: "bagsWithMe", label: "Luggage", hint: "Room for bags." },
  { key: "reserved", label: "Reservations", hint: "Can be booked ahead of time." },
  { key: "airConditioner", label: "Air conditioning", hint: "" },
  { key: "welcomeSign", label: "Welcome sign", hint: "Driver holds a name sign at pickup." },
  { key: "driverAssistant", label: "Driver assistant", hint: "Driver helps with bags or doors." },
  { key: "withInfant", label: "Infants", hint: "Child seat available." },
  { key: "waitTimesInMinutes", label: "Wait time", hint: "Driver will wait, charged by the minute." },
  { key: "tipValue", label: "Tips", hint: "Passengers can add a tip." },
];

const emptyOptions = CAR_OPTIONS.reduce((acc, option) => {
  acc[option.key] = false;
  return acc;
}, {});

const toInputDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");
const SEARCH_DEBOUNCE_MS = 350;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

/**
 * AddCar - register or edit a vehicle. EditCar passes an existing one in.
 *
 * The previous version was 1,851 lines with 52 pieces of state, mostly
 * because every photo slot, every option flag and every dropdown carried
 * its own error state and its own upload handler. Photos are one list
 * per side here, and the option flags are one object.
 */
const AddCar = ({ car, carId }) => {
  const isEdit = Boolean(car);

  const [driverId, setDriverId] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [plate, setPlate] = useState(car ? car.plate || "" : "");
  const [brand, setBrand] = useState(car && car.brand ? car.brand._id : "");
  const [model, setModel] = useState(car && car.model ? car.model._id : "");
  const [color, setColor] = useState(car && car.color ? car.color._id : "");
  const [carType, setCarType] = useState(car && car.carType ? car.carType._id : "");
  const [manufacturingYear, setManufacturingYear] = useState(
    car && car.manufacturingYear ? String(car.manufacturingYear) : ""
  );
  const [description, setDescription] = useState(car ? car.description || "" : "");
  const [ride, setRide] = useState(car ? Boolean(car.ride) : true);
  const [delivery, setDelivery] = useState(car ? Boolean(car.delivery) : false);

  const [innerPhotos, setInnerPhotos] = useState(
    car && car.pictures && car.pictures.inner ? car.pictures.inner.map((p) => p.url) : []
  );
  const [outerPhotos, setOuterPhotos] = useState(
    car && car.pictures && car.pictures.outer ? car.pictures.outer.map((p) => p.url) : []
  );
  const [insuranceUrl, setInsuranceUrl] = useState(
    car && car.insurance ? car.insurance.insuranceImageUrl || "" : ""
  );
  const [insuranceExpiry, setInsuranceExpiry] = useState(
    car && car.insurance ? toInputDate(car.insurance.expireDate) : ""
  );
  const [registrationUrl, setRegistrationUrl] = useState(
    car ? car.registrationDocumentUrl || "" : ""
  );
  const [options, setOptions] = useState(
    car && car.carOptions
      ? CAR_OPTIONS.reduce((acc, option) => {
          acc[option.key] = Boolean(car.carOptions[option.key]);
          return acc;
        }, {})
      : { ...emptyOptions }
  );

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const { data: lookupData, loading: lookupLoading, error: lookupError } = useQuery(GET_LOOKUPS, {
    errorPolicy: "all",
  });
  const [loadModels, { data: modelData }] = useLazyQuery(GET_MODELS);
  const [findDrivers, { data: driverData }] = useLazyQuery(GET_DRIVERS, {
    fetchPolicy: "network-only",
  });

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [addCar] = useMutation(ADD_CAR);
  const [editCar] = useMutation(EDIT_CAR);

  useEffect(() => {
    if (brand) loadModels({ variables: { brand } });
  }, [brand, loadModels]);

  useEffect(() => {
    if (isEdit) return undefined;
    const handler = setTimeout(() => {
      findDrivers({ variables: { fullName: driverSearch.trim() } });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [isEdit, driverSearch, findDrivers]);

  const brandOptions = useMemo(
    () => ((lookupData && lookupData.getCarBrands) || []).map((b) => ({ value: b._id, label: b.name })),
    [lookupData]
  );
  const colorOptions = useMemo(
    () => ((lookupData && lookupData.getCarColors) || []).map((c) => ({ value: c._id, label: c.name })),
    [lookupData]
  );
  const typeOptions = useMemo(
    () => ((lookupData && lookupData.getCarTypes) || []).map((t) => ({ value: t._id, label: t.type })),
    [lookupData]
  );
  const modelOptions = useMemo(
    () => ((modelData && modelData.getCarModels) || []).map((m) => ({ value: m._id, label: m.name })),
    [modelData]
  );
  const driverOptions = useMemo(
    () =>
      ((driverData && driverData.getDriversByAdmin) || []).map((d) => ({
        value: d._id,
        label: d.phoneNumber ? `${d.fullName} · ${d.phoneNumber}` : d.fullName,
      })),
    [driverData]
  );

  const labelFor = (list, value, fallback) => {
    const found = list.find((o) => o.value === value);
    return found ? found.label : fallback;
  };

  // Changing brand invalidates the chosen model.
  const onBrandChange = (value) => {
    setBrand(value);
    setModel("");
  };

  /* ---------------- uploads ---------------- */

  const upload = (file, folderName, slot) => {
    setUploading(slot);
    setFormError("");
    return uploadFile({ variables: { file, folderName } })
      .then((res) => {
        setUploading("");
        return res.data.uploadFile.url;
      })
      .catch((err) => {
        setUploading("");
        setFormError(readableError(err));
        return null;
      });
  };

  const onPickPhotos = (event, slot) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    upload(file, "cars", slot).then((url) => {
      if (!url) return;
      if (slot === "inner") setInnerPhotos((current) => current.concat(url));
      if (slot === "outer") setOuterPhotos((current) => current.concat(url));
      if (slot === "insurance") setInsuranceUrl(url);
      if (slot === "registration") setRegistrationUrl(url);
    });
    event.target.value = "";
  };

  /* ---------------- submit ---------------- */

  const validate = () => {
    const next = {};
    if (!isEdit && !driverId) next.driver = "Choose the driver this vehicle belongs to.";
    if (!brand) next.brand = "Pick a brand.";
    if (!model) next.model = "Pick a model.";
    if (!color) next.color = "Pick a colour.";
    if (!carType) next.carType = "Pick a vehicle type.";
    if (!ride && !delivery) next.use = "A vehicle has to be cleared for rides, deliveries, or both.";
    if (manufacturingYear) {
      const year = parseInt(manufacturingYear, 10);
      const thisYear = new Date().getFullYear();
      if (Number.isNaN(year) || year < 1950 || year > thisYear + 1) {
        next.manufacturingYear = `Enter a year between 1950 and ${thisYear + 1}.`;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving || uploading) return;
    setFormError("");
    if (!validate()) return;

    const common = {
      color,
      brand,
      model,
      carType,
      ride,
      delivery,
      plate: plate.trim() || undefined,
      manufacturingYear: manufacturingYear ? parseInt(manufacturingYear, 10) : undefined,
      description: description.trim() || undefined,
      registrationDocumentUrl: registrationUrl || undefined,
      pictures: {
        inner: innerPhotos.map((url) => ({ url })),
        outer: outerPhotos.map((url) => ({ url })),
      },
      insurance:
        insuranceUrl || insuranceExpiry
          ? {
              insuranceImageUrl: insuranceUrl || undefined,
              expireDate: insuranceExpiry || undefined,
            }
          : undefined,
      carOptions: options,
    };

    setSaving(true);
    const run = isEdit
      ? editCar({ variables: { ...common, carId: carId || car._id } })
      : addCar({ variables: { ...common, driverId } });

    run
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/vehicles`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  /* ---------------- render ---------------- */

  if (lookupLoading) {
    return (
      <div className="page superPeoplePage">
        <main className="main">
          <PageHeader title={isEdit ? "Edit vehicle" : "Add vehicle"} />
          <Skeleton height={280} />
          <Skeleton height={220} />
        </main>
      </div>
    );
  }

  if (lookupError && !lookupData) return <ErrorPage />;

  // A plain function, not a component defined in the render body: as a
  // component it would be a new type on every render, so React would
  // unmount and rebuild the file inputs each time `uploading` changed.
  const photoList = ({ label, urls, onRemove, slot, hint }) => (
    <Field label={label} hint={hint} key={slot}>
      <div className="superPersonImages">
        {urls.map((url) => (
          <span className="superPersonImageButton" key={url} style={{ position: "relative" }}>
            <img src={assetUrl(url)} alt="" />
            <IconButton
              size="small"
              className="rowActionButton"
              onClick={() => onRemove(url)}
              title="Remove"
              style={{ position: "absolute", top: 2, right: 2, background: "rgba(255,255,255,0.9)" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        ))}
        <label className="superPersonFilePicker">
          <input type="file" accept="image/*" hidden onChange={(e) => onPickPhotos(e, slot)} />
          {uploading === slot ? "Uploading…" : "Add photo"}
        </label>
      </div>
    </Field>
  );

  return (
    <div className="page superPeoplePage">
      <main className="main">
        <PageHeader
          title={isEdit ? "Edit vehicle" : "Add vehicle"}
          description={
            isEdit
              ? car && car.plate
                ? `Plate ${car.plate}`
                : ""
              : "Register a vehicle against a driver."
          }
        />

        <form onSubmit={onSubmit}>
          {!isEdit && (
            <Panel title="Driver" subtitle="Who this vehicle belongs to.">
              <div className="superPersonForm">
                <Field label="Find a driver" error={errors.driver} required>
                  <SearchField
                    placeholder="Search drivers by name..."
                    value={driverSearch}
                    onChange={(e) => setDriverSearch(e.target.value)}
                  />
                </Field>
                <FilterDropdown
                  label={labelFor(driverOptions, driverId, "Choose a driver")}
                  options={
                    driverOptions.length
                      ? driverOptions
                      : [{ value: "", label: "No matches — try another name" }]
                  }
                  value={driverId}
                  onChange={(e) => setDriverId(e.target.value)}
                />
              </div>
            </Panel>
          )}

          <Panel title="Vehicle">
            <div className="superPersonForm">
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <div className="superPersonFormRow">
                <Field label="Brand" error={errors.brand} required>
                  <FilterDropdown
                    label={labelFor(brandOptions, brand, "Choose a brand")}
                    options={brandOptions}
                    value={brand}
                    onChange={(e) => onBrandChange(e.target.value)}
                  />
                </Field>

                <Field
                  label="Model"
                  error={errors.model}
                  required
                  hint={brand ? undefined : "Pick a brand first."}
                >
                  <FilterDropdown
                    label={labelFor(modelOptions, model, brand ? "Choose a model" : "Pick a brand first")}
                    options={
                      brand && modelOptions.length
                        ? modelOptions
                        : [{ value: "", label: brand ? "No models for this brand" : "Pick a brand first" }]
                    }
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                </Field>
              </div>

              <div className="superPersonFormRow">
                <Field label="Colour" error={errors.color} required>
                  <FilterDropdown
                    label={labelFor(colorOptions, color, "Choose a colour")}
                    options={colorOptions}
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                  />
                </Field>

                <Field label="Vehicle type" error={errors.carType} required>
                  <FilterDropdown
                    label={labelFor(typeOptions, carType, "Choose a type")}
                    options={typeOptions}
                    value={carType}
                    onChange={(e) => setCarType(e.target.value)}
                  />
                </Field>
              </div>

              <div className="superPersonFormRow">
                <Field label="Plate" htmlFor="plate" optional>
                  <input
                    id="plate"
                    className="dsInput"
                    type="text"
                    value={plate}
                    onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  />
                </Field>

                <Field
                  label="Year"
                  htmlFor="manufacturingYear"
                  error={errors.manufacturingYear}
                  optional
                >
                  <input
                    id="manufacturingYear"
                    className="dsInput"
                    type="number"
                    value={manufacturingYear}
                    onChange={(e) => setManufacturingYear(e.target.value)}
                  />
                </Field>
              </div>

              <Field label="Description" htmlFor="description" optional>
                <textarea
                  id="description"
                  className="dsInput"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <Field label="Cleared for" error={errors.use} required>
                <div className="superPersonForm">
                  <label className={`dsOptionCard ${ride ? "isSelected" : ""}`}>
                    <input type="checkbox" checked={ride} onChange={() => setRide((c) => !c)} />
                    <span className="dsOptionCardText">
                      <strong>Rides</strong>
                      <span>Can carry passengers.</span>
                    </span>
                  </label>
                  <label className={`dsOptionCard ${delivery ? "isSelected" : ""}`}>
                    <input
                      type="checkbox"
                      checked={delivery}
                      onChange={() => setDelivery((c) => !c)}
                    />
                    <span className="dsOptionCardText">
                      <strong>Deliveries</strong>
                      <span>Can carry parcels and shop orders.</span>
                    </span>
                  </label>
                </div>
              </Field>
            </div>
          </Panel>

          <Panel title="Photos and documents">
            <div className="superPersonForm">
              {photoList({
                label: "Exterior photos",
                urls: outerPhotos,
                slot: "outer",
                onRemove: (url) => setOuterPhotos((c) => c.filter((item) => item !== url)),
              })}
              {photoList({
                label: "Interior photos",
                urls: innerPhotos,
                slot: "inner",
                onRemove: (url) => setInnerPhotos((c) => c.filter((item) => item !== url)),
              })}

              <div className="superPersonFormRow">
                {photoList({
                  label: "Insurance document",
                  urls: insuranceUrl ? [insuranceUrl] : [],
                  slot: "insurance",
                  onRemove: () => setInsuranceUrl(""),
                })}
                <Field label="Insurance expires" htmlFor="insuranceExpiry" optional>
                  <input
                    id="insuranceExpiry"
                    className="dsInput"
                    type="date"
                    value={insuranceExpiry}
                    onChange={(e) => setInsuranceExpiry(e.target.value)}
                  />
                </Field>
              </div>

              {photoList({
                label: "Registration document",
                urls: registrationUrl ? [registrationUrl] : [],
                slot: "registration",
                onRemove: () => setRegistrationUrl(""),
              })}
            </div>
          </Panel>

          <Panel title="What this vehicle offers" subtitle="Shown to passengers when they book.">
            <div className="superPersonForm">
              {CAR_OPTIONS.map((option) => (
                <label
                  className={`dsOptionCard ${options[option.key] ? "isSelected" : ""}`}
                  key={option.key}
                >
                  <input
                    type="checkbox"
                    checked={options[option.key]}
                    onChange={() =>
                      setOptions((current) => ({ ...current, [option.key]: !current[option.key] }))
                    }
                  />
                  <span className="dsOptionCardText">
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </Panel>

          <div className="superPersonFooter">
            <Button
              variant="secondary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/vehicles`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} disabled={Boolean(uploading)}>
              {isEdit ? "Save changes" : "Add vehicle"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AddCar;
